import * as React from 'react';
import isNil from 'lodash/isNil';

import { Link } from 'libs/router';

import PageSection from 'components/page/page-section/PageSection';
import ActionButton from 'components/buttons/action-button/ActionButton';
import EntityAvatar from 'components/entity/entity-avatar/EntityAvatar';
import EntityName from 'components/entity/entity-name/EntityName';
import MetricsChannelsBasic from 'components/metrics/channels-basic/MetricsChannelsBasic';
import StatusLabel from 'components/status-label/StatusLabel';

import { __t, applyTranslations } from 'utils/language';
import { getEntityFromProfile } from 'utils/view';
import { prepareDataForMetricsChannelsBasic } from 'utils/metrics';
import { SUBCAMPAIGN_STATE_TO_LABEL_TYPE } from 'utils/subcampaign';
import { renderPrice } from 'utils/helper/price';
import {
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE
} from 'utils/channels';

import './ccm-offer-card-banner.scss';

applyTranslations(require('./ccm-offer-card-banner.i18n.json'));

import type {
  ChannelTypes,
  Subcampaign
} from 'types';
import type {
  CcmOfferTab
} from 'modules/campaigns/components/ccm/components/offers/types';

type Props = {
  campaignId: number,
  subcampaign: Subcampaign,
  activeTab: CcmOfferTab
};

const CHANNELS: Array<ChannelTypes> = [
  CHANNEL_TYPE_INSTAGRAM,
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE
];

const MSG_KEY = 'ccm_offer_card_banner';

const CcmOfferCardBanner: React.FC<Props> = ({
  campaignId,
  subcampaign,
  activeTab
}): JSX.Element | null => {
  if (isNil(subcampaign)) {
    return null;
  }

  const creatorProfile = subcampaign.creatorProfile;
  const entity = getEntityFromProfile(creatorProfile);

  const renderCreatorChannels = (): React.ReactNode => {
    const channelsWithMetrics = CHANNELS.map((channel: ChannelTypes) => (
      prepareDataForMetricsChannelsBasic(creatorProfile, channel))
    );

    return (
      <div className="ccm-offer-card-banner__creator-channels">
        <MetricsChannelsBasic
          channels={channelsWithMetrics}
          showChannels={CHANNELS}
          maxChannels={2}
          type="limited"
          includeRatio
        />
      </div>
    );
  };

  const renderCreator = (): React.ReactNode => (
    <div className="ccm-offer-card-banner__creator">
      <EntityAvatar
        key={entity?.id}
        entity={entity}
        link={entity?.url}
        customSize={{ height: 48, width: 48 }}
        className="ccm-offer-card-banner__creator-avatar"
        openInNewTab
        hideTitle
        bordered
      />
      <div className="ccm-offer-card-banner__creator-details">
        <EntityName
          className="ccm-offer-card-banner__creator-name text-overflow-ellipsis"
          entity={entity}
          link={entity?.url}
          openInNewTab
        />
        {
          creatorProfile.channels &&
            renderCreatorChannels()
        }
      </div>
    </div>
  );

  const renderDetails = (): React.ReactNode => (
    <div className="ccm-offer-card-banner__details">
      <div>
        <span>
          {__t(`${MSG_KEY}.total_outfits`)}:
        </span>
        {subcampaign.outfitPerformance?.submittedOutfits ?? 0}
      </div>
      <div>
        <span>
          {__t(`${MSG_KEY}.total_affiliated_compensation`)}:
        </span>
        {renderPrice(subcampaign.outfitPerformance?.totalAffiliatedCompensation ?? 0)}
      </div>
      <div>
        <span>
          {__t(`${MSG_KEY}.total_outfit_clicks`)}:
        </span>
        {subcampaign.outfitPerformance?.totalOutfitClicks ?? 0}
      </div>
    </div>
  );

  const renderStatus = (): React.ReactNode => {
    const labelType = activeTab === 'revoked'
      ? 'danger'
      : SUBCAMPAIGN_STATE_TO_LABEL_TYPE[activeTab];

    return (
      <div className="ccm-offer-card-banner__status">
        <StatusLabel
          size="nano"
          label={__t(`${MSG_KEY}.status_label.${activeTab}`)}
          type={labelType}
        />
      </div>
    );
  };

  const renderButtons = (): React.ReactNode => (
    <Link to={`/campaigns/${campaignId}/messages?subcampaignId=${subcampaign.id}`}>
      <ActionButton
        width="maximum"
        color="primary"
      >
        {__t(`${MSG_KEY}.buttons.message_creator`)}
      </ActionButton>
    </Link>
  );

  return (
    <PageSection
      style="volume"
      padding="none"
      margin="none"
      border="none"
    >
      <div className="ccm-offer-card-banner">
        {renderCreator()}
        {renderDetails()}
        <div className="ccm-offer-card-banner__actions">
          {renderStatus()}
          <div className="ccm-offer-card-banner__buttons">
            {renderButtons()}
          </div>
        </div>
      </div>
    </PageSection>
  );
};

export default CcmOfferCardBanner;
